import React, { useState } from 'react';

function Section({ title, children, note }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      <div className="p-4 space-y-3">
        {children}
        {note ? (
          <div className="text-xs text-gray-500 border-t pt-3">{note}</div>
        ) : null}
      </div>
    </div>
  );
}

function PlaceholderCard({ title, desc, cta }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 bg-white flex flex-col gap-2">
      <div className="text-sm font-semibold text-gray-800">{title}</div>
      {desc ? <div className="text-sm text-gray-600">{desc}</div> : null}
      {cta ? <div className="text-xs text-gray-500">{cta}</div> : null}
    </div>
  );
}

export default function PoCShowcase() {
  const [teamSize, setTeamSize] = useState(5);
  const [hourlyCost, setHourlyCost] = useState(800); // ₺/saat varsayılan
  const [usSmbTotal, setUsSmbTotal] = useState(1000000); // Tahmini US SMB e‑ticaret site sayısı
  const [euSmbTotal, setEuSmbTotal] = useState(1200000); // Tahmini Avrupa SMB e‑ticaret site sayısı

  // Baseline (Geleneksel) ve Glowytics süreleri (saat)
  const baseline = {
    // Daha temkinli tahminler: haftada toplam ~10–15 saat
    reporting: { min: 4, max: 6 },
    optimization: { min: 3, max: 4 },
    management: { min: 1, max: 2 },
    creative: { min: 2, max: 3 },
  };
  const glowytics = {
    reporting: { min: 0.0, max: 0.25 }, // 0–15 dk
    optimization: { min: 1, max: 2 },
    management: { min: 0, max: 0 }, // app/alert
    creative: { min: 0.25, max: 0.25 }, // ~15 dk
  };

  const sumRange = (obj) =>
    Object.values(obj).reduce(
      (acc, v) => ({ min: acc.min + v.min, max: acc.max + v.max }),
      { min: 0, max: 0 }
    );
  const baseTotal = sumRange(baseline);     // ~15–25 saat
  const glowTotal = sumRange(glowytics);    // ~1.5–3.5 saat

  const avg = (r) => (r.min + r.max) / 2;
  const weeklySavedPerPerson = Math.max(0, avg(baseTotal) - avg(glowTotal));
  const weeklySavedTeam = weeklySavedPerPerson * teamSize;
  const weeklyCostSaved = weeklySavedTeam * hourlyCost;
  const annualCostSaved = weeklyCostSaved * 52;

  const pctTimeSaving = Math.round(((avg(baseTotal) - avg(glowTotal)) / avg(baseTotal)) * 100);

  const fmtHrs = (v) => `${v.toFixed(1)}s`;
  const fmtTL = (v) => `₺${Math.round(v).toLocaleString('tr-TR')}`;

  return (
    <div className="p-6 bg-[#F7F8FC] min-h-screen space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Glowytics — Proof of Concept Showcase</h1>
          <p className="text-sm text-gray-500 mt-1">
            Hakem sunumu için PoC içeriklerini derlemek üzere hazırlanan sayfa. Bana içerikleri ver; her bölümü dolduralım.
          </p>
        </div>
      </div>

      {/* Summary header cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="text-xs text-gray-500">PoC Seti</div>
          <div className="text-lg font-semibold text-gray-800 mt-1">Genel Özet</div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="text-xs text-gray-500">Durum</div>
          <div className="text-lg font-semibold text-gray-800 mt-1">Hazırlanıyor</div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="text-xs text-gray-500">Versiyon</div>
          <div className="text-lg font-semibold text-gray-800 mt-1">v0.1</div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="text-xs text-gray-500">Not</div>
          <div className="text-lg font-semibold text-gray-800 mt-1">İçerik bekleniyor</div>
        </div>
      </div>

      {/* Glowytics on the Go — One App Replacing Many */}
      {(() => {
        const AppBadge = ({ name, bg, fg, icon }) => (
          <div className="flex items-center gap-3 bg-white border rounded-xl p-3 shadow-sm">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: bg }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                {icon(fg)}
              </svg>
            </div>
            <div className="text-sm font-medium text-gray-800">{name}</div>
          </div>
        );
        const apps = [
          {
            key: 'GOOGLE_ADS',
            name: 'Google Ads',
            bg: '#E3F2FD',
            fg: '#1A73E8',
            icon: (c) => (
              <>
                <path d="M5 19a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" fill="#34A853" />
                <path d="M10 5l6.5 13H13L6.5 5H10Z" fill={c} />
              </>
            ),
          },
          {
            key: 'META_ADS',
            name: 'Meta Ads',
            bg: '#E8F0FF',
            fg: '#0866FF',
            icon: (c) => (
              <path
                d="M5 12c1.5-4 4-6 6.2-4.5 1 .6 1.8 1.8 2.8 3.6 1.2 2 2.3 3.4 3.9 3.9-1.5 4-4 6-6.2 4.5-1-.6-1.8-1.8-2.8-3.6C7.8 13.9 6.7 12.5 5 12Z"
                stroke={c}
                strokeWidth="1.6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ),
          },
          {
            key: 'TIKTOK_ADS',
            name: 'TikTok Ads',
            bg: '#FCE4EC',
            fg: '#EE1D52',
            icon: (c) => (
              <>
                <path d="M14 4c1.3 2 2.7 3 5 3v3c-2.2 0-3.8-.7-5-1.6V16a4 4 0 1 1-4-4h1v3h-1a1 1 0 1 0 1 1V4h3Z" fill={c} />
                <circle cx="9" cy="17" r="0.75" fill="#69C9D0" />
              </>
            ),
          },
          {
            key: 'GA4',
            name: 'Google Analytics 4',
            bg: '#FFF3E0',
            fg: '#F9AB00',
            icon: (c) => (
              <>
                <rect x="3" y="12" width="3" height="8" rx="1" fill={c} />
                <rect x="8.5" y="9" width="3" height="11" rx="1" fill={c} />
                <rect x="14" y="6" width="3" height="14" rx="1" fill={c} />
                <circle cx="20" cy="5" r="2" fill={c} />
              </>
            ),
          },
          {
            key: 'SHOPIFY',
            name: 'Shopify',
            bg: '#E8F5E9',
            fg: '#1B873E',
            icon: (c) => (
              <>
                <path d="M7 6l10-2 1 14-9 2-2-10 0-4Z" fill={c} opacity="0.18" />
                <path d="M8 8l1.5 8 7-1-0.8-10-7.7 1.6Z" stroke={c} strokeWidth="1.4" fill="none" />
                <path d="M12.5 9.5c-.8-.5-2 .2-2 1.2 0 .8.7 1.1 1.4 1.3.8.2 1.1.5 1.1.9 0 .5-.5.9-1.1.9-.5 0-1-.1-1.3-.4" stroke={c} strokeWidth="1.4" fill="none" strokeLinecap="round" />
              </>
            ),
          },
        ];
        return (
          <Section title="Glowytics on the Go — One App, All Marketing">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              {/* Left: Source apps (all) */}
              <div className="flex flex-col gap-3">
                {apps.map((a) => (
                  <AppBadge key={a.key} name={a.name} bg={a.bg} fg={a.fg} icon={a.icon} />
                ))}
                <div className="text-[11px] text-gray-500 mt-1">
                  Bu 5 ayrı uygulamadaki veriyi tek bir mobil uygulamada birleştiriyoruz.
                </div>
              </div>
              {/* Right: Single Glowytics mobile mock */}
              <div className="flex items-center justify-center">
                <div className="relative">
                  {/* Glow halo */}
                  <div className="absolute inset-0 blur-2xl rounded-[32px] bg-gradient-to-br from-indigo-200 via-purple-200 to-pink-200 -z-10" />
                  {/* Mobile mock */}
                  <div className="w-72 rounded-[28px] border border-gray-200 bg-white shadow-xl overflow-hidden">
                    <div className="h-6 bg-black/90"></div>
                    <div className="p-4 bg-gradient-to-b from-white to-gray-50">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-black flex items-center justify-center">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M4 12a8 8 0 1 1 16 0c0 4.4-3.6 8-8 8-1.8 0-3.5-.6-4.8-1.6l1.8-2.4A5.9 5.9 0 0 0 12 18c3.3 0 6-2.7 6-6s-2.7-6-6-6a6 6 0 0 0-5.9 5.1L4 12Z" fill="white" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Glowytics On The Go</div>
                          <div className="text-xs text-gray-500">Tek uygulama — tüm pazarlama verisi</div>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-3 gap-2 text-[11px]">
                        <div className="rounded-lg border p-2 bg-white">
                          <div className="text-[10px] text-gray-500">ROAS</div>
                          <div className="font-semibold text-gray-900">4.8x</div>
                        </div>
                        <div className="rounded-lg border p-2 bg-white">
                          <div className="text-[10px] text-gray-500">Revenue</div>
                          <div className="font-semibold text-gray-900">₺1.2M</div>
                        </div>
                        <div className="rounded-lg border p-2 bg-white">
                          <div className="text-[10px] text-gray-500">Alerts</div>
                          <div className="font-semibold text-gray-900">3</div>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-gray-600">
                        Google Ads, Meta Ads, TikTok Ads, GA4 ve Shopify verilerini tek yerde birleştirir; karar hızını artırır.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        );
      })()}

      {/* Operational Efficiency Impact */}
      <Section title="Operational Efficiency Impact (Weekly)">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Baseline vs Glowytics tables */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              Geleneksel
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-[11px] text-gray-600">
                  <th className="text-left py-2 px-3">Operasyon</th>
                  <th className="text-right py-2 px-3">Haftalık (Kişi)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-t">
                  <td className="py-2 px-3">Raporlama</td>
                  <td className="py-2 px-3 text-right">{baseline.reporting.min}–{baseline.reporting.max} s</td>
                </tr>
                <tr className="border-t">
                  <td className="py-2 px-3">Optimizasyon / Kampanya Bakımı</td>
                  <td className="py-2 px-3 text-right">{baseline.optimization.min}–{baseline.optimization.max} s</td>
                </tr>
                <tr className="border-t">
                  <td className="py-2 px-3">Sunum / Yönetim Güncellemeleri</td>
                  <td className="py-2 px-3 text-right">{baseline.management.min}–{baseline.management.max} s</td>
                </tr>
                <tr className="border-t">
                  <td className="py-2 px-3">Kreatif Analiz / Metin Testleri</td>
                  <td className="py-2 px-3 text-right">{baseline.creative.min}–{baseline.creative.max} s</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 text-sm font-semibold">
                  <td className="py-2 px-3">Toplam (Kişi)</td>
                  <td className="py-2 px-3 text-right">{baseTotal.min}–{baseTotal.max} s</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              Glowytics ile
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-[11px] text-gray-600">
                  <th className="text-left py-2 px-3">Operasyon</th>
                  <th className="text-right py-2 px-3">Haftalık (Kişi)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-t">
                  <td className="py-2 px-3">Raporlama</td>
                  <td className="py-2 px-3 text-right">0–0.25 s (tam otomatik)</td>
                </tr>
                <tr className="border-t">
                  <td className="py-2 px-3">Optimizasyon / Kampanya Bakımı</td>
                  <td className="py-2 px-3 text-right">1–2 s (AI önerileri)</td>
                </tr>
                <tr className="border-t">
                  <td className="py-2 px-3">Sunum / Yönetim Güncellemeleri</td>
                  <td className="py-2 px-3 text-right">0 s (mobil & Slack)</td>
                </tr>
                <tr className="border-t">
                  <td className="py-2 px-3">Kreatif Analiz / Metin Testleri</td>
                  <td className="py-2 px-3 text-right">0.25 s (AI insights)</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 text-sm font-semibold">
                  <td className="py-2 px-3">Toplam (Kişi)</td>
                  <td className="py-2 px-3 text-right">{glowTotal.min}–{glowTotal.max} s</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* KPI Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Zaman Tasarrufu (Kişi)</div>
            <div className="text-xl font-semibold text-gray-900 mt-1">{fmtHrs(weeklySavedPerPerson)}</div>
            <div className="text-xs text-gray-500 mt-1">Haftalık ortalama</div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Zaman Tasarrufu (Ekip)</div>
            <div className="text-xl font-semibold text-gray-900 mt-1">{fmtHrs(weeklySavedTeam)}</div>
            <div className="text-xs text-gray-500 mt-1">{teamSize} kişi</div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Haftalık Maliyet Tasarrufu</div>
            <div className="text-xl font-semibold text-gray-900 mt-1">{fmtTL(weeklyCostSaved)}</div>
            <div className="text-xs text-gray-500 mt-1">@ {fmtTL(hourlyCost)}/saat</div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Yıllık Projeksiyon</div>
            <div className="text-xl font-semibold text-gray-900 mt-1">{fmtTL(annualCostSaved)}</div>
            <div className="text-xs text-gray-500 mt-1">~{pctTimeSaving}% zaman tasarrufu</div>
          </div>
        </div>

        {/* Calculator */}
        <div className="mt-4 bg-white rounded-xl border p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Ekip Büyüklüğü (kişi)</label>
            <input
              type="number"
              value={teamSize}
              min={1}
              className="w-full border rounded-md px-3 py-2 text-sm"
              onChange={(e) => setTeamSize(Math.max(1, Number(e.target.value)))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Saatlik Maliyet (₺)</label>
            <input
              type="number"
              value={hourlyCost}
              min={0}
              className="w-full border rounded-md px-3 py-2 text-sm"
              onChange={(e) => setHourlyCost(Math.max(0, Number(e.target.value)))}
            />
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              Bu değerler ekip deneyimlerimize dayalı tahminlerdir; gerçek kullanımda sektöre/olgunluğa göre değişebilir.
            </div>
          </div>
        </div>
      </Section>

      {/* SMB E‑commerce Landscape — US vs Europe */}
      {(() => {
        const usPlatforms = [
          { name: 'WooCommerce', share: '35–40%' },
          { name: 'Shopify', share: '25–30%' },
          { name: 'Wix / Squarespace', share: '20–25%' },
          { name: 'BigCommerce / Magento', share: '5–8%' },
          { name: 'Custom / Other', share: '8–12%' },
        ];
        const euPlatforms = [
          { name: 'WooCommerce', share: '35–45%' },
          { name: 'Shopify', share: '15–20%' },
          { name: 'PrestaShop', share: '8–12%' },
          { name: 'Shopware / Magento / BigCommerce', share: '5–8%' },
          { name: 'Custom / Other', share: '10–15%' },
        ];
        const midpoint = (pct) => {
          const m = pct.match(/(\d+)[–-](\d+)%/);
          if (m) {
            const a = Number(m[1]), b = Number(m[2]);
            return (a + b) / 2;
          }
          return Number(pct.replace('%', '')) || 0;
        };
        const Bar = ({ pct, label, count }) => (
          <div className="w-full">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span className="truncate">{label}</span>
              <span className="ml-2 whitespace-nowrap">{pct}{count != null ? ` • ~${count.toLocaleString('en-US')}` : ''}</span>
            </div>
            <div className="mt-1 h-2 bg-gray-100 rounded">
              <div
                className="h-2 rounded bg-gray-800"
                style={{
                  width: `${midpoint(pct)}%`
                }}
              />
            </div>
          </div>
        );
        return (
          <Section
            title="SMB E‑commerce Landscape — US vs Europe"
            note={
              <>
                Bu dağılımlar site sayısı bazında yaklaşık göstergelerdir; ülkeye/segmentlere göre değişebilir.
                Kaynaklar (genel eğilimler):{' '}
                <a className="text-blue-600 hover:underline" href="https://w3techs.com/technologies/overview/ecommerce" target="_blank" rel="noreferrer">
                  W3Techs — E‑commerce systems usage
                </a>{' '}•{' '}
                <a className="text-blue-600 hover:underline" href="https://trends.builtwith.com/shop" target="_blank" rel="noreferrer">
                  BuiltWith — eCommerce usage distribution
                </a>
              </>
            }
          >
            {/* Assumptions / Inputs */}
            <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-white border rounded-xl p-3">
                <div className="text-xs text-gray-600 mb-1">Estimated SMB e‑commerce site count — United States</div>
                <input
                  type="number"
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={usSmbTotal}
                  min={0}
                  onChange={(e) => setUsSmbTotal(Math.max(0, Number(e.target.value)))}
                />
              </div>
              <div className="bg-white border rounded-xl p-3">
                <div className="text-xs text-gray-600 mb-1">Estimated SMB e‑commerce site count — Europe</div>
                <input
                  type="number"
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  value={euSmbTotal}
                  min={0}
                  onChange={(e) => setEuSmbTotal(Math.max(0, Number(e.target.value)))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* US */}
              <div className="bg-white rounded-xl border p-4">
                <div className="text-sm font-semibold text-gray-900">United States (by merchant count)</div>
                <div className="text-xs text-gray-500 mt-1">SMB oranı (site sayısı bazında): ~95–98%</div>
                <div className="mt-3 space-y-2">
                  {usPlatforms.map((r) => {
                    const cnt = Math.round(usSmbTotal * (midpoint(r.share) / 100));
                    return <Bar key={r.name} pct={r.share} label={r.name} count={cnt} />;
                  })}
                </div>
              </div>
              {/* Europe */}
              <div className="bg-white rounded-xl border p-4">
                <div className="text-sm font-semibold text-gray-900">Europe (by merchant count)</div>
                <div className="text-xs text-gray-500 mt-1">SMB oranı (site sayısı bazında): ~95–98%</div>
                <div className="mt-3 space-y-2">
                  {euPlatforms.map((r) => {
                    const cnt = Math.round(euSmbTotal * (midpoint(r.share) / 100));
                    return <Bar key={r.name} pct={r.share} label={r.name} count={cnt} />;
                  })}
                </div>
              </div>
            </div>
          </Section>
        );
      })()}

      {/* Eksikler ve Glowytics’in Yanıtı (Tablo) */}
      <Section title="Eksikler ve Glowytics’in Yanıtı">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-[11px] text-gray-600">
                <th className="text-left py-2 px-3">Alan</th>
                <th className="text-left py-2 px-3">Pain / Gap</th>
                <th className="text-left py-2 px-3">Etki</th>
                <th className="text-left py-2 px-3">Glowytics Çözümü</th>
                <th className="text-left py-2 px-3">Örnek Çıktı</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {[
                ['Araç Parçalanması','Google/Meta/TikTok/GA4/Shopify ayrı, veri zinciri kopuk','Dağınık karar, zaman kaybı','Tek doğruluk katmanı, birleşik şema, bağlantı sağlık izleme','Tek panel + On‑The‑Go mobil'],
                ['Manuel Raporlama','Haftalık saatler tablo/slide üretimine gidiyor','Operasyon maliyeti, hata riski','Otomatik rapor, schedule, white‑label PDF','C‑level “morning brief”'],
                ['Karar Gecikmesi','Uyarı/aksiyon zinciri yok','Geç müdahale, fırsat kaybı','Anomali/SoV alert + tek‑tık playbook','“ROAS < hedef → bütçe kaydır”'],
                ['Tutarlılık/Güven','Kaynak metrikler uyuşmuyor','Güven erozyonu','Sync log, idempotent upsert, deterministik paging','Rows processed/inserted/updated'],
                ['Ürün Görünürlüğü','Ürün/SKU etkisi belirsiz','Yanlış yatırım','Product‑level ROAS, kategori/ürün/variant','Kazanan/kaybeden ürünler'],
                ['Stok/Variant Sağlığı','Stok‑out/variant karmaşıklığı görünmüyor','Boş harcama, kaçan satış','Stok‑farkındalıklı bastırma/bid','“Stok‑out ürünleri baskıla”'],
                ['Pazar Zekâsı','Rakip fiyat/promosyon/kreatif/trend yok','Geç kalınan hamle','Competitive Radar','“Rakip X %25 indirim → teklif/mesaj ayarla”'],
                ['Attribution','Çoklu dokunuş katkısı bilinmiyor','Bütçe optimizasyonu zayıf','Multi‑touch modeller','Kanal/ürün katkı matrisi'],
                ['Kreatif İçgörü','Mesaj/format ne çalışıyor belirsiz','Düşük CTR/CR','Kreatif taxonomy + performans panosu','Hook/CTA/format skorları'],
                ['Aktivasyon Boşluğu','“Gördüm→Yaptım” arası uzun','Uygulama gecikmesi','Playbook + güvenli push + audit','Kim/ne/zaman kayıtlı değişiklik'],
                ['SMB Erişimi','Kendi başına kurulum zor','Düşük penetrasyon','5‑adım wizard, demo mode, self‑billing','10 dk’da değer anı'],
                ['Kurumsal Gereksinimler','RBAC, audit, SLA, webhooks','Güven eksikliği','Enterprise katmanı','Rol/izin seti, audit viewer, SLA'],
              ].map((row, idx) => (
                <tr key={idx} className="border-t align-top">
                  {row.map((cell, i) => (
                    <td key={i} className="py-2 px-3">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Ürün Odaklı Raporlar */}
      <Section title="Ürün Odaklı Raporlar">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="text-[11px] text-gray-600">
                <th className="text-left py-2 px-3">Rapor</th>
                <th className="text-left py-2 px-3">Metrik / Dim.</th>
                <th className="text-left py-2 px-3">Karar</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {[
                ['Product‑level ROAS','Revenue, Cost, ROAS; Ürün/Kategori/Marka','Bütçeyi ürün ağırlıklı dağıt'],
                ['Variant/Stok Sağlığı','Stok‑out, back‑in‑stock, variant coverage','Stok‑farkındalıklı bastır/boost'],
                ['Fiyat/Marj Elastikiyeti','Fiyat değişimi vs dönüşüm/ROAS','İndirim/markup stratejisi'],
                ['Assortment Coverage','Kategori‑ürün ısı haritası','Ürün gamını genişlet/daralt'],
                ['Feed Sağlığı','MC hata/policy, attribute eksikleri','Feed düzeltme öncelik listesi'],
                ['Arama Niyeti → Ürün','Search intent → ürün eşlemesi','Landing/ürün eşleşmesini iyileştir'],
                ['Cross/Up‑sell','Birlikte alışlar, sepet kombinasyonları','Bundle/öneri blokları'],
                ['Kampanya→Ürün Etkisi','Kampanya/AdGroup→ürün gelir','Kanal/ürün yatırım matrisi'],
                ['Rakip‑Ürün Radar','Rakip fiyat/promosyon/kreatif','Karşı hamle ve mesaj güncelle'],
              ].map((row, idx) => (
                <tr key={idx} className="border-t align-top">
                  {row.map((cell, i) => (
                    <td key={i} className="py-2 px-3">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Narrative Flow (Q&A Timeline) */}
      {(() => {
        const central = 'Glowytics Value Story';
        const qaFlow = [
          {
            q: 'Who Benefits?',
            a: [
              'CMO / Marketing Director',
              'Performance Team',
              'E‑commerce Manager',
              'Agency Partner',
              'SMB Owner (Shopify/Eticaret)',
              'CEO / Founder (karar hızı)',
              'CFO / Finance (verim & ROI)',
              'BI / Analytics (tek doğruluk katmanı)',
              'Product / CRM (segment & LTV)',
              'Retail / Store Ops (lokal performans)',
              'Creative Team (içgörü & varyant test)',
            ],
          },
          {
            q: 'The Problems We Solve',
            a: [
              'Manuel Raporlama Kaosu',
              'Geç Karar / Onay Akışı',
              'Veri Dağınıklığı ve Tutarsızlık',
              'Verimsiz Optimizasyon Döngüsü',
              'Kreatif İçgörü Eksikliği',
              'Silo araçlar (Ads/GA4/MC/SC ayrık)',
              'Bütçe kaydırma gecikmesi',
              'Attribution belirsizliği',
              'Kreatif taxonomy/etiket eksikliği',
              'Backfill darboğazları',
              'Yönetici güncelleme/özet gecikmesi',
            ],
          },
          {
            q: 'Core Impact',
            a: [
              '15–25s → 1.5–3.5s/hafta',
              'Decision latency ↓ %90',
              'Anlık KPI & Alerting',
              'AI Campaign Suggestions',
              'Auto Executive Updates',
              'Waste reduction (low‑ROAS kapama)',
              'One‑click playbooks (bütçe/teklif)',
              'Creative insights → hızlı varyant',
              'Revenue / ROAS Lift',
              'Merchandising insights (kategori/ürün/variant)',
              'Assortment & pricing fırsatları',
              'Stok‑farkındalıklı bidding/suppression',
              'Cross‑sell / upsell önerileri',
              'RFM segmentasyonu (Recency‑Frequency‑Monetary)',
              'Lifecycle & win‑back tetikleyicileri',
              'Audience building: churn risk / high‑LTV / VIP',
            ],
          },
          {
            q: 'Data Reliability & Governance',
            a: [
              'Tek Şema / Çoklu Marka',
              'Role‑based access',
              'Kaynak Doğrulama & Sağlık',
              'Audit & Sync Log',
              'Deterministic order (itemId, itemName)',
              'Backfill Stratejisi (Daily/Monthly)',
              'Idempotent upsert',
              'Monitoring & uyarılar',
              'Source‑of‑truth sözleşmeleri',
            ],
          },
        ];
        return (
          <Section title="Narrative Flow (Q&A Timeline)">
            <div className="relative overflow-x-auto py-8">
              {/* horizontal spine */}
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-0.5 bg-gray-200"></div>
              <div className="relative flex items-start gap-8 min-w-[960px]">
                {/* Central topic */}
                <div className="flex items-center">
                  <div className="bg-red-500 text-white rounded-xl px-4 py-2 text-sm font-semibold shadow">
                    {central}
                  </div>
                </div>
                {qaFlow.map((step, idx) => (
                  <div key={idx} className="flex flex-col items-start min-w-[260px]">
                    {/* Question card on the spine */}
                    <div className="relative mb-2">
                      <div className="bg-white border rounded-xl px-4 py-2 shadow-sm text-gray-800 font-medium">
                        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-semibold rounded-full bg-gray-800 text-white mr-2">
                          {idx + 1}
                        </span>
                        {step.q}
                      </div>
                    </div>
                    {/* Connector */}
                    <div className="w-px h-6 bg-gray-300 ml-6"></div>
                    {/* Answers */}
                    <div className="mt-2 flex flex-col gap-2">
                      {step.a.map((ans, j) => (
                        <div
                          key={j}
                          className="inline-flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-1 text-xs text-gray-700"
                        >
                          {ans}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        );
      })()}

      {/* Target Firms & Monetization */}
      {(() => {
        const [enterpriseCountDefault, smbCountDefault] = [50, 900];
        const [enterprisePriceDefault, smbPriceDefault] = [1200, 150];
        // Local derived (kept static for now; reuse page state if needed)
        const ent = enterpriseCountDefault;
        const smb = smbCountDefault;
        const entPrice = enterprisePriceDefault;
        const smbPrice = smbPriceDefault;
        const mrr = ent * entPrice + smb * smbPrice;
        const arr = mrr * 12;
        return (
          <Section title="Target Firms & Monetization">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="text-[11px] text-gray-600">
                    <th className="text-left py-2 px-3">Segment</th>
                    <th className="text-left py-2 px-3">Who</th>
                    <th className="text-left py-2 px-3">Key Value</th>
                    <th className="text-left py-2 px-3">How It Works</th>
                    <th className="text-right py-2 px-3">Pricing (Monthly)</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-t">
                    <td className="py-2 px-3 font-semibold text-gray-900">Enterprise E‑commerce</td>
                    <td className="py-2 px-3">Büyük e‑ticaret markaları, in‑house ekip + ajans</td>
                    <td className="py-2 px-3">
                      Raporlama tek noktadan; “görmediklerini gör” ürün/merchandising raporları; C‑level anlık özet;
                      ajansa karşı el güçlendiren benchmark & içgörüler; bütçe/optimizasyon playbook’ları; veri yönetişimi.
                    </td>
                    <td className="py-2 px-3">
                      Customer Success Manager ile devreye alma; veri kaynakları bağlanır; otomatik raporlar ve
                      yönetsel özetler açılır; AI önerileriyle sprint bazlı iyileştirme.
                    </td>
                    <td className="py-2 px-3 text-right">₺ ayarlanmamış / $ {entPrice.toLocaleString('en-US')} (1000–1500$ aralığı)</td>
                  </tr>
                  <tr className="border-t">
                    <td className="py-2 px-3 font-semibold text-gray-900">SMB (Shopify/Ikas/Woo)</td>
                    <td className="py-2 px-3">Küçük/orta markalar, tek kişi marketing</td>
                    <td className="py-2 px-3">
                      Desteksiz kolay kurulum; Glowytics içinden önerilen Meta/Google kampanyaları; doğru hedef kitle
                      otomasyonu; küçük “ajans” gücüyle kampanyayı kur‑yönet; basit merchandising içgörüleri.
                    </td>
                    <td className="py-2 px-3">
                      App içinde sihirbaz: mağaza bağla → önerilen kampanyayı seç → audience & bütçe otomatik;
                      haftalık özet + uyarılar.
                    </td>
                    <td className="py-2 px-3 text-right">$ {smbPrice.toLocaleString('en-US')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Static projection per brief (50 enterprise, 900 SMB) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-xl border p-4">
                <div className="text-xs text-gray-500">Projection — Enterprise</div>
                <div className="text-lg font-semibold text-gray-900 mt-1">{ent} müşteri × ${entPrice.toLocaleString('en-US')}</div>
              </div>
              <div className="bg-white rounded-xl border p-4">
                <div className="text-xs text-gray-500">Projection — SMB</div>
                <div className="text-lg font-semibold text-gray-900 mt-1">{smb} müşteri × ${smbPrice.toLocaleString('en-US')}</div>
              </div>
              <div className="bg-white rounded-xl border p-4">
                <div className="text-xs text-gray-500">MRR / ARR</div>
                <div className="text-lg font-semibold text-gray-900 mt-1">
                  MRR: ${mrr.toLocaleString('en-US')} — ARR: ${arr.toLocaleString('en-US')}
                </div>
              </div>
            </div>
          </Section>
        );
      })()}

      {/* What's Next: Planned Features & New Revenue Streams */}
      <Section title="What’s Next — Planned Features & New Revenue Streams">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Messaging Suite */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              Messaging Suite (Email / SMS / Push)
            </div>
            <div className="p-4 text-sm text-gray-800 space-y-2">
              <ul className="list-disc pl-5 space-y-1">
                <li>Email marketing: SendGrid / Mailgun / Mailchimp entegrasyonları</li>
                <li>SMS marketing: Twilio / MessageBird / Turkcell‑Vodafone entegrasyonları</li>
                <li>Web/App Push bildirimleri (Chrome/Safari + mobil SDK)</li>
                <li>Audience selection: RFM, churn risk, VIP, kategori/tüketim eğilimi</li>
                <li>Otomatik akışlar: welcome, win‑back, cart/browse abandonment, replenishment</li>
                <li>Dinamik içerik: stok geri geldi, fiyat düştü, kişiselleştirilmiş ürün</li>
                <li>KPI: Delivery/CTR/CTO/CR, kanal bazlı lift ve marj koruması</li>
                <li>Gelir modeli: paket + kullanım (ör. $99–$299/ay + mesaj/adet maliyeti)</li>
              </ul>
            </div>
          </div>

          {/* On‑Site Conversion Suite */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              On‑Site Conversion (CRO & Personalization)
            </div>
            <div className="p-4 text-sm text-gray-800 space-y-2">
              <ul className="list-disc pl-5 space-y-1">
                <li>Product Recommendations Engine: bestsellers/trending/CF‑tabanlı</li>
                <li>Kişiselleştirme blokları: anasayfa, kategori, PDP, sepet</li>
                <li>Cart boosters: free‑shipping threshold, cross‑sell, kupon guardrails</li>
                <li>Experimentation: A/B ve holdout ölçümleme, guardrail KPI’ları</li>
                <li>SDK/snippet: Shopify/Ikas/Woo temalarına tek tık yerleşim</li>
                <li>Data privacy & consent: CMP/UTM/consent state ile uyum</li>
                <li>Gelir modeli: $149–$499/ay (trafik ve blok sayısına göre)</li>
              </ul>
            </div>
          </div>

          {/* Feed Management & PIM (Priority) */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              Feed Management & PIM <span className="ml-2 inline-flex items-center px-2 py-0.5 text-[10px] rounded-md bg-emerald-100 text-emerald-700">Priority</span>
            </div>
            <div className="p-4 text-sm text-gray-800 space-y-2">
              <ul className="list-disc pl-5 space-y-1">
                <li>Merchant Center health: policy/hata tespiti, otomatik düzeltme önerileri</li>
                <li>Feed zenginleştirme: kategori/taxonomy eşleme, eksik attribute doldurma</li>
                <li>Görsel/başlık/description optimizasyonu; UTM ve GTIN/MPN kontrolü</li>
                <li>Fiyat & stok senkronizasyonu, varyant birleştirme</li>
                <li>Multi‑channel export: Google MC, Meta Catalog, TikTok, Criteo vb.</li>
                <li>Alerting & sürümleme: feed değişikliklerinde geri alma, QA checklist</li>
                <li>Gelir modeli: 99–299$/ay (ürün sayısı ve kanal sayısına göre)</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Audience‑Driven Orchestration</div>
            <div className="text-sm text-gray-700 mt-1">
              “Kim‑ne zaman‑hangi mesaj?” sorusunu RFM + davranış sinyalleri ile otomatik belirleyip kampanya
              aktivasyonunu tek panelden yönetme.
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Incrementality & Lift</div>
            <div className="text-sm text-gray-700 mt-1">
              Messaging + on‑site önerilerin marj ve ROAS üzerindeki artı etkisini kontrollü testlerle kanıtlama.
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Roadmap Uyum</div>
            <div className="text-sm text-gray-700 mt-1">
              Mevcut entegrasyonlar (GA4/Ads/Shopify/Ikas/Woo) sayesinde segmentasyon hazır; üstüne aktivasyon ve
              site‑içi ürünleri eklemek hızlı.
            </div>
          </div>
        </div>
      </Section>

      {/* Chronology — From Today to Target State */}
      {(() => {
        const ent = 50, smb = 900, entPrice = 1200, smbPrice = 150;
        const mrr = ent * entPrice + smb * smbPrice;
        const arr = mrr * 12;
        const steps = [
          {
            when: 'Today',
            title: 'Unified Analytics',
            color: 'bg-blue-600',
            card: [
              'GA4 + Ads + Shopify tek panel',
              'Sync Logs & idempotent upsert',
              'AI öneri temelleri',
            ],
          },
          {
            when: 'Q1 2026',
            title: 'Self‑Serve Foundations',
            color: 'bg-blue-600',
            card: [
              'Shopify/Ikas/Woo kolay onboarding',
              'Playbook’lar ve uyarılar',
              'Mobile “On The Go”',
            ],
          },
          {
            when: 'H2 2026',
            title: 'Activation Layer',
            color: 'bg-blue-600',
            card: [
              'Messaging Suite (Email/SMS/Push)',
              'Feed Mgmt & PIM (Priority)',
              'CRO & Personalization',
            ],
          },
          {
            when: 'Target',
            title: 'SMB‑First Scale',
            color: 'bg-emerald-600',
            highlight: true,
            card: [
              `50 Enterprise + ${smb} SMB`,
              `MRR $${mrr.toLocaleString('en-US')} / ARR $${arr.toLocaleString('en-US')}`,
              'Az efor, yüksek marjlı büyüme',
            ],
          },
        ];
        return (
          <Section title="Chronology — From Today to SMB‑First Target">
            <div className="relative overflow-x-auto py-6">
              {/* timeline spine */}
              <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-blue-100"></div>
              <div className="relative flex items-start gap-10 min-w-[980px]">
                {steps.map((s, i) => (
                  <div key={i} className="flex flex-col items-center min-w-[220px]">
                    {/* marker */}
                    <div className="flex items-center justify-center">
                      <div className={`w-10 h-10 ${s.color} rounded-full ring-8 ring-white shadow`} />
                    </div>
                    {/* label */}
                    <div className="mt-2 text-[11px] uppercase tracking-wide text-gray-500">{s.when}</div>
                    {/* card */}
                    <div className={`mt-2 w-full bg-white border rounded-xl p-3 ${s.highlight ? 'border-emerald-200 shadow-md' : 'border-gray-200'}`}>
                      <div className={`text-sm font-semibold ${s.highlight ? 'text-emerald-700' : 'text-gray-800'}`}>
                        {s.title}
                      </div>
                      <ul className="mt-1 text-xs text-gray-600 space-y-1 list-disc pl-4">
                        {s.card.map((line, j) => (
                          <li key={j}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        );
      })()}

      {/* Signature Timeline (Stylized) */}
      {(() => {
        const milestones = [
          {
            year: '2025',
            title: 'Platform Stabilizasyonu',
            subtitle: 'Perf/Cache/Security',
            side: 'below',
            color: '#5179B8',
            icon: (c) => (
              <path d="M12 7l3 3-6 6H6v-3l6-6Z" stroke={c} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            ),
          },
          {
            year: '2026 • Q1',
            title: 'E‑ticaret Entegrasyonları',
            subtitle: 'Shopify & Woo',
            side: 'above',
            color: '#7C5DA7',
            icon: (c) => (
              <>
                <rect x="7" y="7" width="10" height="10" rx="2" stroke={c} strokeWidth="1.6" fill="none" />
                <path d="M9 12h6M12 9v6" stroke={c} strokeWidth="1.6" strokeLinecap="round" />
              </>
            ),
          },
          {
            year: '2026 • Q2',
            title: 'Self‑Serve & Mobile v2',
            subtitle: 'Onboarding + Push',
            side: 'below',
            color: '#5179B8',
            icon: (c) => (
              <path d="M8 6h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" stroke={c} strokeWidth="1.6" fill="none" />
            ),
          },
          {
            year: '2026 • H2',
            title: 'Enterprise Özellikleri',
            subtitle: 'RBAC • Reports • SLA',
            side: 'above',
            color: '#7C5DA7',
            icon: (c) => (
              <>
                <path d="M7 10h10M7 14h10" stroke={c} strokeWidth="1.6" strokeLinecap="round" />
                <rect x="6" y="7" width="12" height="10" rx="2" stroke={c} strokeWidth="1.6" fill="none" />
              </>
            ),
          },
          {
            year: '2027',
            title: 'SMB‑First Scale',
            subtitle: '900 SMB + 50 Ent.',
            side: 'below',
            color: '#5179B8',
            icon: (c) => (
              <path d="M6 14l4 4 8-8" stroke={c} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            ),
          },
        ];
        const W = 1200;
        const H = 300;
        const padX = 60;
        const yTrack = 150;
        const seg = (W - padX * 2) / (milestones.length + 0.5);
        const xs = milestones.map((_, i) => padX + seg * (i + 0.5));
        const barHeight = 28;
        const segRects = milestones.map((m, i) => {
          const x0 = i === 0 ? padX : (xs[i - 1] + xs[i]) / 2;
          const x1 = i === milestones.length - 1 ? W - padX : (xs[i] + xs[i + 1]) / 2;
          return { x: x0, w: x1 - x0, color: m.color };
        });
        return (
          <Section title="Signature Roadmap — Stylized Ribbon">
            <div className="overflow-x-auto">
              <svg viewBox={`0 0 ${W} ${H}`} className="min-w-[960px] w-full h-[320px]">
                <defs>
                  <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="4" stdDeviation="6" floodOpacity="0.2" />
                  </filter>
                </defs>
                {segRects.map((r, idx) => (
                  <rect
                    key={idx}
                    x={r.x}
                    y={yTrack - barHeight / 2}
                    width={r.w}
                    height={barHeight}
                    rx={barHeight / 2}
                    fill={r.color}
                    filter="url(#shadow)"
                  />
                ))}
                {milestones.map((m, i) => {
                  const x = xs[i];
                  const isAbove = m.side === 'above';
                  const bubbleY = isAbove ? yTrack - 86 : yTrack + 86;
                  const textY = isAbove ? bubbleY - 10 : bubbleY + 58;
                  return (
                    <g key={i}>
                      <path
                        d={`M ${x} ${isAbove ? yTrack - 14 : yTrack + 14} L ${x} ${isAbove ? bubbleY + 24 : bubbleY - 24}`}
                        stroke="#D1D5DB"
                        strokeWidth="2"
                      />
                      <g transform={`translate(${x - 24},${bubbleY - 24})`}>
                        <circle cx="24" cy="24" r="24" fill="white" />
                        <circle cx="24" cy="24" r="22" fill="#F3F4F6" />
                        <svg x="12" y="12" width="24" height="24" viewBox="0 0 24 24">
                          {m.icon('#4B5563')}
                        </svg>
                      </g>
                      <g transform={`translate(${x - 56},${yTrack - 22})`}>
                        <rect width="112" height="44" rx="22" fill="white" />
                        <text x="56" y="28" textAnchor="middle" fontSize="16" fontWeight="700" fill="#111827">
                          {m.year}
                        </text>
                      </g>
                      <text x={x} y={textY} textAnchor="middle" fontSize="14" fontWeight="700" fill="#111827">
                        {m.title}
                      </text>
                      <text x={x} y={textY + (isAbove ? 18 : -18)} textAnchor="middle" fontSize="12" fill="#6B7280">
                        {m.subtitle}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </Section>
        );
      })()}

      {/* Roadmap Chronology — Work Packages (Scope Summary) */}
      {(() => {
        const wps = [
          {
            code: 'WP1',
            title: 'Platform Stabilizasyonu & Performans',
            start: '01.12.2025',
            end: '31.01.2026',
            bullets: [
              'Frontend/Backend optimizasyon, caching, izleme',
              'Query tuning, index, partition; cache TTL stratejisi',
              'APM + logging; güvenlik kontrolleri (JWT, rate limit)',
            ],
          },
          {
            code: 'WP2',
            title: 'E‑Ticaret Entegrasyonları (Shopify & WooCommerce)',
            start: '01.02.2026',
            end: '30.04.2026',
            bullets: [
              'OAuth, webhook, GraphQL/REST API, pagination',
              'Unified katalog ve order‑source eşlemesi',
              'Product‑level ROAS ve attribution temelleri',
            ],
          },
          {
            code: 'WP2a',
            title: 'Ads Activation Suite (Google & Meta Ads)',
            start: '01.03.2026',
            end: '15.05.2026',
            bullets: [
              'Hesaplara bağlan, kampanya yarat/yönet/optimize',
              'Bütçe/teklif playbook’ları, idempotent push, audit',
              'Policy/compliance ve katalog ile senkron aktivasyon',
            ],
            accent: 'blue',
          },
          {
            code: 'WP3',
            title: 'Self‑Service Onboarding & Mobile App v2',
            start: '01.05.2026',
            end: '30.06.2026',
            bullets: [
              '5 adım sihirbaz, health checker, demo mode',
              'Stripe billing; push notifications, biometric, offline',
              'Time‑to‑value <10 dk; DAU +%25 hedefi',
            ],
          },
          {
            code: 'WP4',
            title: 'Enterprise Özellikleri',
            start: '01.07.2026',
            end: '31.08.2026',
            bullets: [
              'RBAC, audit trail, multi‑brand yönetimi',
              'Custom report & scheduled report, white‑label PDF',
              'Public API, webhooks, SLA çerçevesi',
            ],
          },
          {
            code: 'WP5',
            title: 'Customer Success Platform & Internal CRM',
            start: '01.09.2026',
            end: '31.10.2026',
            bullets: [
              'Health score, churn risk uyarıları, playbook otomasyon',
              'In‑app messaging, ticketing entegrasyonları',
              'CS dashboard, journey analytics',
            ],
          },
          {
            code: 'WP6',
            title: 'Advanced AI & Predictive Analytics',
            start: '01.11.2026',
            end: '31.12.2026',
            bullets: [
              'Churn, forecast, anomaly; budget optimizer',
              'Multi‑model LLM routing, insight generation',
              'MAPE/accuracy hedefleri ve prod izleme',
            ],
          },
          {
            code: 'WP7',
            title: 'Multi‑Language & Global Expansion',
            start: '01.01.2027',
            end: '28.02.2027',
            bullets: [
              'i18n, çoklu para birimi ve timezone desteği',
              'GDPR/KVKK süreçleri, localized website',
              'Uluslararası pilot: 15 müşteri',
            ],
          },
          {
            code: 'WP8',
            title: 'Advanced Reporting & Analytics Engine',
            start: '01.03.2027',
            end: '30.04.2027',
            bullets: [
              'Report builder, cohort/funnel, Sheets sync',
              'SQL builder (read‑only), 12+ yeni görselleştirme',
              'Template library ve planlanmış exportlar',
            ],
          },
          {
            code: 'WP9',
            title: 'Data Governance, Compliance & Security',
            start: '01.05.2027',
            end: '31.05.2027',
            bullets: [
              'Data governance, retention, secrets management',
              '2FA/MFA, pentest, SOC2 başlatma',
              'DR planı, compliance dashboard',
            ],
          },
        ];
        return (
          <Section title="Roadmap Chronology — Work Packages (Scope Summary)">
            <div className="relative">
              {/* vertical spine */}
              <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
              <div className="space-y-4 pl-10">
                {wps.map((w, i) => (
                  <div key={i} className="relative">
                    {/* marker */}
                    <div
                      className={`absolute -left-6 top-2 w-3.5 h-3.5 rounded-full ${
                        w.accent === 'blue' ? 'bg-blue-600' : 'bg-gray-800'
                      } ring-4 ring-white`}
                    />
                    {/* card */}
                    <div className="bg-white border border-gray-200 rounded-xl p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-gray-900">
                          {w.code}: {w.title}
                        </div>
                        <div className="text-[11px] text-gray-600">
                          {w.start} → {w.end}
                        </div>
                      </div>
                      <ul className="mt-1 text-xs text-gray-700 list-disc pl-4 space-y-1">
                        {w.bullets.map((b, j) => (
                          <li key={j}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        );
      })()}

      {/* Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="1) Data Integrations (GA4 / Ads / Adjust / Ikas / Shopify)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PlaceholderCard
              title="Bağlantı Doğrulama"
              desc="OAuth / API token yenileme, bağlantı sağlık göstergeleri."
            />
            <PlaceholderCard
              title="Veri Aktarım Logları"
              desc="Batch/stream çekimler; satır sayısı, hatalar ve retry."
            />
            <PlaceholderCard
              title="Şema / Çoklu Marka"
              desc="brand_{name}_{id} şema izolasyonu, erişim kontrolü."
            />
            <PlaceholderCard
              title="Örnek API Yanıtları"
              desc="Kritik endpointlerden örnek payload/response."
            />
          </div>
        </Section>

        <Section title="2) Analytics & Experimentation">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PlaceholderCard
              title="GA4 Platform/Device"
              desc="Daily/Monthly kırılım; compact sayı formatı, filtreler."
            />
            <PlaceholderCard
              title="Revenue Forecast — Demo"
              desc="Basit trend + hedef karşılaştırma görselleştirmesi."
            />
            <PlaceholderCard
              title="Attribution / Funnel"
              desc="Örnek akış ekranları (funnel step, conversion path)."
            />
            <PlaceholderCard
              title="Adjust Summary"
              desc="Bağlı ise özet kart; ikon ve bağlantı bilgisi."
            />
            <PlaceholderCard
              title="RFM & Audience Segments"
              desc="Geçmiş satın alma verilerinden RFM sınıflandırma; VIP, Dormant, At‑risk, Win‑back segmentleri."
            />
            <PlaceholderCard
              title="Cohort / LTV & CLV"
              desc="Kohort bazlı gelir, tekrar satın alma, LTV eğrisi; hedefe göre kampanya planı."
            />
          </div>
        </Section>

        <Section title="3) Budget & Media Planning PoCs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PlaceholderCard
              title="Branding vs Performance"
              desc="Aylık dağılım, CPM hedefleri, toplam/frekans özetleri."
            />
            <PlaceholderCard
              title="Objective Breakdown"
              desc="Campaign objective bazında metrik özetleri."
            />
            <PlaceholderCard
              title="Targeting Örnekleri"
              desc="Programmatic / YouTube / Display / TV targeting listeleri."
            />
            <PlaceholderCard
              title="Asset Örnekleri"
              desc="HTML5 banner set, bumper/skippable, co-brand KV."
            />
          </div>
        </Section>

        <Section title="4) Backfill & Limits (GA4 E‑commerce Item Monthly)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PlaceholderCard
              title="Pagination >10K"
              desc="Offset/limit ve sıralama ile deterministik paging."
            />
            <PlaceholderCard
              title="Sync Log"
              desc="Aylık satır sayısı, süre, hata/başarı log örnekleri."
            />
            <PlaceholderCard
              title="DB Yapısı"
              desc="ga_ecommerce_item_monthly tablosu ve indeksler."
            />
            <PlaceholderCard
              title="UI Trigger"
              desc="Settings > Data Cleanup alanından tarih aralığı ile tetikleme."
            />
          </div>
        </Section>
      </div>

      {/* Competitive & Market Insights */}
      <Section title="Competitive & Market Insights — Fast Market Intelligence">
        {/* Neutral header */}
        <div className="rounded-xl overflow-hidden border bg-white">
          <div className="px-6 py-5">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Market Radar</div>
            <div className="mt-1 text-xl font-semibold text-gray-900">Rakip ve pazar sinyallerinden dakikalar içinde aksiyona</div>
            <div className="mt-2 text-sm text-gray-700">
              Kreatif, fiyat/promosyon, arama trendleri ve SoV verilerini birleştirerek “ne olmuş?”u değil “ne yapmalı?”yı verir.
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] bg-gray-100 text-gray-700">Time‑to‑Insight &lt; 5m</span>
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] bg-gray-100 text-gray-700">SoV & Trend Alerts</span>
              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] bg-gray-100 text-gray-700">One‑click Playbooks</span>
            </div>
          </div>
        </div>

        {/* Content cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              Inputs
            </div>
            <div className="p-4 text-sm text-gray-800">
              <ul className="list-disc pl-5 space-y-1">
                <li>Rakip kreatifleri (Meta/Google/YouTube kütüphaneleri)</li>
                <li>Fiyat/promosyon/stok sinyalleri (pazar yerleri & web)</li>
                <li>Arama trendleri & kategori hacmi (Search Console/Trends)</li>
                <li>Sosyal paylaşımlar & Share‑of‑Voice sinyalleri</li>
              </ul>
            </div>
          </div>
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              Signals → Insights
            </div>
            <div className="p-4 text-sm text-gray-800">
              <ul className="list-disc pl-5 space-y-1">
                <li>SoV değişimi ve artış/düşüş hız göstergesi</li>
                <li>Kategori trendinde ani yükseliş/çöküş uyarıları</li>
                <li>Rakip fiyat düşüşü/promosyon atağı algılama</li>
                <li>Yükselen kreatif format/mesaj tespiti</li>
              </ul>
            </div>
          </div>
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-600 uppercase tracking-wide">
              Actions
            </div>
            <div className="p-4 text-sm text-gray-800">
              <ul className="list-disc pl-5 space-y-1">
                <li>Slack “Morning Brief”: rakip/market özetleri</li>
                <li>Tek tık playbook: bütçe kaydır, teklif güncelle, kreatif varyant aç</li>
                <li>Fiyat/kupon guardrails önerileri (marj korumalı)</li>
                <li>SoV hedefi için kampanya & kreatif öneri listesi</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Mini stats */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Time‑to‑Insight</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">Dakikalar</div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">SoV / Trend Capture</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">Anlık fırsat uyarıları</div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Actionability</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">Tek tık playbook</div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-xs text-gray-500">Add‑on Model</div>
            <div className="text-lg font-semibold text-gray-900 mt-1">$99–$199/ay</div>
          </div>
        </div>
      </Section>

      <Section
        title="Notes & Next Steps"
        note="Bu şablona istediğin başlıkları/ekran görüntülerini/grafikleri ekleyelim. İnteraktif demolar için ayrı alt sayfalar da bağlayabilirim."
      >
        <div className="text-sm text-gray-700">
          İçerikleri gönderdiğinde, her bir kutuyu gerçek veri/grafik/tablolarla dolduracağım. Eklemek istediğin özel bir PoC başlığı varsa söyle.
        </div>
      </Section>
    </div>
  );
}


